import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { definePathwayInputSchema, revisePathwayInputSchema } from '@cc/schema';
import { useMemo, useState, type ReactNode } from 'react';
import {
  RELEASE_RULES,
  STEP_KINDS,
  draftProblem,
  freshStep,
  inputOfDraft,
  moved,
  sectionKey,
  type PathwayDraft,
  type ReleaseRuleName,
  type SectionDraft,
  type StepDraft,
} from './model';
import { StepTargetPicker } from './StepTargetPicker';
import { usePathwayCommand } from './usePathwayCommand';

/**
 * E34 · the pathway Builder — "Practice — Pathways — Builder" (cc-homework, chapter "Pathways — Define &
 * enroll (portal, E34)", screen e34e0004-0001-4000-8000-000000000001) and its "Save changes (section)"
 * (e34e0004-…-0008): name, description, visibility, and sections of steps; a step editor at the side sets
 * kind, target, title, instructions, the release rule (with its day / delay) and the due days.
 *
 * Behavior lines implemented here:
 *   · gesture: Save pathway → DefinePathway (new, or a copy) / RevisePathway (existing), i18n
 *     pathways.builder.*; a revision first shows the banner that names the running enrolments staying on
 *     their version ("Save as version N?"), i18n pathways.builder.saveChanges;
 *   · the Target picker is the assignment drawer's (`StepTargetPicker`);
 *   · a step with rule afterPrevious in first position is flagged inline before Save;
 *   · format: a copy (Duplicate) opens here with "Copied from …" above the name and " (copy)" after it;
 *     Save records DefinePathway with copiedFrom, i18n pathways.builder.copiedFrom;
 *   · help: visibility public says "CopingCard reviews every public course before it reaches Courses" and
 *     is offered only while the practice holds publish courses (otherwise disabled, the reason beside
 *     it); a refusal PATHWAY_PUBLISH_NOT_ALLOWED shows as the server's line, i18n
 *     pathways.builder.visibility.publicHelp;
 *   · a `video` / `session` step (Q-e34-24 A, E46) is not offered yet — the phone cannot open it before
 *     its block (question Q-b3-5); a pathway that carries one keeps it, read-only here.
 * A coded refusal is shown verbatim above the Save button; nothing autosaves.
 */
type Props = {
  mode: 'define' | 'revise';
  initial: PathwayDraft;
  pathwayId: string | null;
  /** The loaded pathway's version (a revision is version + 1). */
  version: number | null;
  /** The enrolments still running (the Save changes banner). */
  runningEnrollments: number;
  /** The practice may make a course public (publish courses). */
  mayPublish: boolean;
  heading: ReactNode;
  onSaved: (pathwayId: string) => void;
  onCancel: () => void;
};

export const PathwayBuilder = ({
  mode,
  initial,
  pathwayId,
  version,
  runningEnrollments,
  mayPublish,
  heading,
  onSaved,
  onCancel,
}: Props) => {
  const t = useTranslate();
  const { message } = AntdApp.useApp();
  const { run } = usePathwayCommand();
  const [draft, setDraft] = useState<PathwayDraft>(initial);
  const [selected, setSelected] = useState<string | null>(
    initial.sections[0]?.steps[0]?.stepId ?? null,
  );
  const [problem, setProblem] = useState<string | null>(null);
  const [problemStep, setProblemStep] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const allSteps = useMemo(() => draft.sections.flatMap((s) => s.steps), [draft]);
  const selectedStep = allSteps.find((s) => s.stepId === selected) ?? null;
  const firstStepId = allSteps[0]?.stepId ?? null;

  const setSections = (f: (sections: SectionDraft[]) => SectionDraft[]) =>
    setDraft((d) => ({ ...d, sections: f(d.sections) }));

  const updateStep = (stepId: string, patch: Partial<StepDraft>) =>
    setSections((sections) =>
      sections.map((s) => ({
        ...s,
        steps: s.steps.map((st) => (st.stepId === stepId ? { ...st, ...patch } : st)),
      })),
    );

  const addStep = (sectionIndex: number) => {
    setSections((sections) => {
      const step = freshStep(sections, sections.flatMap((s) => s.steps).length === 0);
      setSelected(step.stepId);
      return sections.map((s, i) => (i === sectionIndex ? { ...s, steps: [...s.steps, step] } : s));
    });
  };

  const addSection = () =>
    setSections((sections) => [...sections, { key: sectionKey(), section: '', steps: [] }]);

  const removeStep = (stepId: string) => {
    setSections((sections) =>
      sections.map((s) => ({ ...s, steps: s.steps.filter((st) => st.stepId !== stepId) })),
    );
    if (selected === stepId) setSelected(null);
  };

  const moveStep = (sectionIndex: number, index: number, by: -1 | 1) =>
    setSections((sections) =>
      sections.map((s, i) => (i === sectionIndex ? { ...s, steps: moved(s.steps, index, by) } : s)),
    );

  const releaseLabel = (st: StepDraft) => {
    switch (st.rule) {
      case 'onDay':
        return t('pathways.release.onDayN', { day: st.day ?? 0 });
      case 'afterPrevious':
        return t('pathways.release.afterPreviousN', { count: st.delayDays ?? 0 });
      default:
        return t(`pathways.release.${st.rule}`);
    }
  };

  const save = async (confirmed: boolean) => {
    setProblem(null);
    setProblemStep(null);
    const missing = draftProblem(draft);
    if (missing) {
      setProblem(t(missing.key));
      setProblemStep(missing.stepId ?? null);
      if (missing.stepId) setSelected(missing.stepId);
      return;
    }
    if (mode === 'revise' && !confirmed) {
      setConfirming(true);
      return;
    }
    const body = inputOfDraft(draft);
    const input =
      mode === 'revise'
        ? { pathwayId, ...body }
        : { ...body, copiedFrom: draft.copiedFrom };
    try {
      await (mode === 'revise' ? revisePathwayInputSchema : definePathwayInputSchema).validate(input);
    } catch {
      setProblem(t('pathways.builder.invalid'));
      return;
    }
    setSaving(true);
    const r = await run(
      mode === 'revise' ? 'revisePathway' : 'definePathway',
      input,
      t('pathways.builder.saveFailed'),
    );
    setSaving(false);
    setConfirming(false);
    if (!r.ok) {
      setProblem(r.message);
      return;
    }
    message.success(
      mode === 'revise'
        ? t('pathways.builder.savedVersion', { version: (r.data.version as number) ?? '' })
        : t('pathways.builder.saved'),
    );
    onSaved(String(r.data.pathwayId));
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }} data-testid="pathway-builder">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        {heading}
        <Space>
          <Button onClick={onCancel} data-testid="pathway-builder-cancel">
            {t('pathways.builder.cancel')}
          </Button>
          <Button type="primary" loading={saving} onClick={() => save(false)} data-testid="pathway-builder-save">
            {t('pathways.builder.save')}
          </Button>
        </Space>
      </Space>

      {draft.copiedFrom ? (
        <Typography.Text type="secondary" data-testid="pathway-builder-copied-from">
          {t('pathways.builder.copiedFrom', { name: draft.copiedFromName ?? draft.copiedFrom })}
        </Typography.Text>
      ) : null}

      {problem ? (
        <Alert type="error" showIcon message={problem} data-testid="pathway-builder-problem" />
      ) : null}

      <Row gutter={16}>
        <Col xs={24} lg={15}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text>{t('pathways.builder.name')}</Typography.Text>
                <Input
                  value={draft.name}
                  maxLength={200}
                  placeholder={t('pathways.builder.namePlaceholder')}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  data-testid="pathway-builder-name"
                />
                <Typography.Text>{t('pathways.builder.description')}</Typography.Text>
                <Input.TextArea
                  value={draft.description}
                  maxLength={2000}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  placeholder={t('pathways.builder.descriptionPlaceholder')}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  data-testid="pathway-builder-description"
                />
                <Typography.Text>{t('pathways.builder.visibility.label')}</Typography.Text>
                <Radio.Group
                  value={draft.visibility === 'prescribeOnly' ? 'practice' : draft.visibility}
                  onChange={(e) => setDraft((d) => ({ ...d, visibility: e.target.value }))}
                  data-testid="pathway-builder-visibility"
                >
                  <Radio value="practice" data-testid="pathway-builder-visibility-practice">
                    {t('pathways.visibility.practice')}
                  </Radio>
                  <Radio value="public" disabled={!mayPublish} data-testid="pathway-builder-visibility-public">
                    {t('pathways.visibility.public')}
                  </Radio>
                </Radio.Group>
                <Typography.Text type="secondary" style={{ fontSize: 12 }} data-testid="pathway-builder-public-help">
                  {mayPublish
                    ? t('pathways.builder.visibility.publicHelp')
                    : t('pathways.builder.visibility.publicUnavailable')}
                </Typography.Text>
              </Space>
            </Card>

            <Typography.Title level={5} style={{ margin: 0 }}>
              {t('pathways.builder.sections')}
            </Typography.Title>
            {draft.sections.map((section, si) => (
              <Card
                key={section.key}
                size="small"
                data-testid={`pathway-builder-section-${si}`}
                title={
                  <Input
                    value={section.section}
                    maxLength={200}
                    placeholder={t('pathways.builder.sectionPlaceholder')}
                    onChange={(e) =>
                      setSections((sections) =>
                        sections.map((s, i) => (i === si ? { ...s, section: e.target.value } : s)),
                      )
                    }
                    data-testid={`pathway-builder-section-title-${si}`}
                  />
                }
                extra={
                  draft.sections.length > 1 && section.steps.length === 0 ? (
                    <Button
                      size="small"
                      type="text"
                      icon={<DeleteOutlined />}
                      aria-label={t('pathways.builder.removeSection')}
                      onClick={() => setSections((sections) => sections.filter((_, i) => i !== si))}
                      data-testid={`pathway-builder-section-remove-${si}`}
                    />
                  ) : null
                }
              >
                <Table<StepDraft>
                  rowKey="stepId"
                  size="small"
                  pagination={false}
                  dataSource={section.steps}
                  locale={{ emptyText: t('pathways.builder.noSteps') }}
                  onRow={(st) =>
                    ({
                      'onClick': () => setSelected(st.stepId),
                      'data-testid': `pathway-builder-step-${st.stepId}`,
                      'style': {
                        cursor: 'pointer',
                        background: st.stepId === selected ? '#f0f5ff' : undefined,
                      },
                    } as never)
                  }
                  columns={[
                    { title: '#', width: 48, render: (_, st) => st.stepId },
                    {
                      title: t('pathways.builder.column.step'),
                      render: (_, st) => (
                        <Space direction="vertical" size={0}>
                          <span>{st.title || t('pathways.builder.untitled')}</span>
                          {st.stepId === firstStepId && st.rule === 'afterPrevious' ? (
                            <Typography.Text type="danger" style={{ fontSize: 12 }} data-testid={`pathway-builder-first-rule-${st.stepId}`}>
                              {t('pathways.builder.firstStepRule')}
                            </Typography.Text>
                          ) : null}
                          {problemStep === st.stepId ? (
                            <Typography.Text type="danger" style={{ fontSize: 12 }}>
                              {problem}
                            </Typography.Text>
                          ) : null}
                        </Space>
                      ),
                    },
                    {
                      title: t('pathways.builder.column.kind'),
                      width: 120,
                      render: (_, st) => t(`homework.kind.${st.kind}`),
                    },
                    {
                      title: t('pathways.builder.column.release'),
                      width: 150,
                      render: (_, st) => (
                        <span data-testid={`pathway-builder-step-release-${st.stepId}`}>{releaseLabel(st)}</span>
                      ),
                    },
                    {
                      title: t('pathways.builder.column.due'),
                      width: 80,
                      render: (_, st) =>
                        st.dueDays === null ? '—' : t('pathways.builder.dueDaysShort', { count: st.dueDays }),
                    },
                    {
                      title: '',
                      width: 108,
                      render: (_, st, index) => (
                        <Space size={0} onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            type="text"
                            icon={<ArrowUpOutlined />}
                            aria-label={t('pathways.builder.moveUp')}
                            disabled={index === 0}
                            onClick={() => moveStep(si, index, -1)}
                          />
                          <Button
                            size="small"
                            type="text"
                            icon={<ArrowDownOutlined />}
                            aria-label={t('pathways.builder.moveDown')}
                            disabled={index === section.steps.length - 1}
                            onClick={() => moveStep(si, index, 1)}
                          />
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label={t('pathways.builder.removeStep')}
                            onClick={() => removeStep(st.stepId)}
                            data-testid={`pathway-builder-step-remove-${st.stepId}`}
                          />
                        </Space>
                      ),
                    },
                  ]}
                />
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8 }}
                  onClick={() => addStep(si)}
                  data-testid={`pathway-builder-add-step-${si}`}
                >
                  {t('pathways.builder.addStep')}
                </Button>
              </Card>
            ))}
            <Button icon={<PlusOutlined />} onClick={addSection} data-testid="pathway-builder-add-section">
              {t('pathways.builder.addSection')}
            </Button>
          </Space>
        </Col>

        <Col xs={24} lg={9}>
          <Card
            size="small"
            title={
              selectedStep
                ? t('pathways.builder.editor.title', { stepId: selectedStep.stepId })
                : t('pathways.builder.editor.none')
            }
            data-testid="pathway-step-editor"
          >
            {selectedStep ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text>{t('pathways.builder.editor.kind')}</Typography.Text>
                {selectedStep.keptTarget ? (
                  <Tag data-testid="pathway-step-kept">{t(`homework.kind.${selectedStep.kind}`)}</Tag>
                ) : (
                  <Select
                    style={{ width: '100%' }}
                    // every kind on screen at once — seven do not fit antd's 256 px list, which then opens scrolled to the selected one
                    listHeight={STEP_KINDS.length * 44}
                    value={selectedStep.kind}
                    options={STEP_KINDS.map((k) => ({ value: k, label: t(`homework.kind.${k}`) }))}
                    onChange={(kind: string) => updateStep(selectedStep.stepId, { kind, targetRef: null })}
                    data-testid="pathway-step-kind"
                  />
                )}
                <Typography.Text>{t('pathways.builder.editor.target')}</Typography.Text>
                {selectedStep.keptTarget ? (
                  <Typography.Text type="secondary" data-testid="pathway-step-kept-target">
                    {selectedStep.keptTarget.ref ?? selectedStep.keptTarget.type}
                  </Typography.Text>
                ) : (
                  <StepTargetPicker
                    kind={selectedStep.kind}
                    value={selectedStep.targetRef}
                    testId="pathway-step-target"
                    onChange={(targetRef, suggested) =>
                      updateStep(selectedStep.stepId, {
                        targetRef,
                        ...(suggested && !selectedStep.title.trim() ? { title: suggested } : {}),
                      })
                    }
                  />
                )}
                <Typography.Text>{t('pathways.builder.editor.stepTitle')}</Typography.Text>
                <Input
                  value={selectedStep.title}
                  maxLength={200}
                  placeholder={t('pathways.builder.editor.stepTitlePlaceholder')}
                  onChange={(e) => updateStep(selectedStep.stepId, { title: e.target.value })}
                  data-testid="pathway-step-title"
                />
                <Typography.Text>{t('pathways.builder.editor.instructions')}</Typography.Text>
                <Input.TextArea
                  value={selectedStep.instructions}
                  maxLength={4000}
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  onChange={(e) => updateStep(selectedStep.stepId, { instructions: e.target.value })}
                  data-testid="pathway-step-instructions"
                />
                <Typography.Text>{t('pathways.builder.editor.release')}</Typography.Text>
                <Select
                  style={{ width: '100%' }}
                  value={selectedStep.rule}
                  options={RELEASE_RULES.map((r) => ({ value: r, label: t(`pathways.release.${r}`) }))}
                  onChange={(rule: ReleaseRuleName) =>
                    updateStep(selectedStep.stepId, {
                      rule,
                      day: rule === 'onDay' ? selectedStep.day ?? 7 : null,
                      delayDays: rule === 'afterPrevious' ? selectedStep.delayDays ?? 0 : null,
                    })
                  }
                  data-testid="pathway-step-release"
                />
                {selectedStep.rule === 'onDay' ? (
                  <Space>
                    <Typography.Text>{t('pathways.builder.editor.day')}</Typography.Text>
                    <InputNumber
                      min={0}
                      max={365}
                      value={selectedStep.day ?? 0}
                      onChange={(v) => updateStep(selectedStep.stepId, { day: v === null ? null : Number(v) })}
                      data-testid="pathway-step-day"
                    />
                  </Space>
                ) : null}
                {selectedStep.rule === 'afterPrevious' ? (
                  <Space>
                    <Typography.Text>{t('pathways.builder.editor.delayDays')}</Typography.Text>
                    <InputNumber
                      min={0}
                      max={365}
                      value={selectedStep.delayDays ?? 0}
                      onChange={(v) => updateStep(selectedStep.stepId, { delayDays: v === null ? 0 : Number(v) })}
                      data-testid="pathway-step-delay"
                    />
                  </Space>
                ) : null}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t(`pathways.release.help.${selectedStep.rule}`)}
                </Typography.Text>
                <Space>
                  <Typography.Text>{t('pathways.builder.editor.dueDays')}</Typography.Text>
                  <InputNumber
                    min={0}
                    max={365}
                    value={selectedStep.dueDays ?? undefined}
                    placeholder={t('pathways.builder.editor.noDue')}
                    onChange={(v) =>
                      updateStep(selectedStep.stepId, { dueDays: v === null || v === undefined ? null : Number(v) })
                    }
                    data-testid="pathway-step-due"
                  />
                </Space>
              </Space>
            ) : (
              <Typography.Text type="secondary">{t('pathways.builder.editor.pick')}</Typography.Text>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        open={confirming}
        title={t('pathways.builder.saveChanges.title', { version: (version ?? 0) + 1 })}
        okText={t('pathways.builder.saveChanges.confirm', { version: (version ?? 0) + 1 })}
        cancelText={t('pathways.builder.saveChanges.back')}
        okButtonProps={{ loading: saving, 'data-testid': 'pathway-save-changes-confirm' } as never}
        onOk={() => save(true)}
        onCancel={() => setConfirming(false)}
        destroyOnClose
      >
        <Typography.Paragraph data-testid="pathway-save-changes-banner">
          {t('pathways.builder.saveChanges.banner', {
            count: runningEnrollments,
            version: version ?? 1,
            next: (version ?? 0) + 1,
          })}
        </Typography.Paragraph>
      </Modal>
    </Space>
  );
};
